/* revenueByLeaf.js -- THE REVENUE FORECAST BY INCOME-STATEMENT LINE: ONE OWNER (2026-10-27, Brian B1 / B2;
 * diffs/pct-revenue-base-investigation.txt).
 *
 * WHY THIS EXISTS: a vendor's "% of Total Revenue" multiplied runFullModel's combinedRevenue (AE + Company Quota GAAP only),
 * while the Income Statement's Total Revenue row also carries every misc and implementation line -- apart by the routed misc line in
 * every forecast month on all five companies, the same words meaning two numbers. And a vendor could not be a % of an
 * implementation line at all.
 * The revenue each module puts on each IS line was computed in TWO places: runFullModel's forecast merge (misc + implementation)
 * and the orchestrator's injection (AE + Company Quota + implementation again). Both now take their series from here, and the
 * vendor engine reads the same map BEFORE it runs, so the IS and a vendor's base cannot disagree.
 *
 * THE TWO LAYERS, KEPT EXACTLY AS THEY RAN (order and rounding unchanged):
 *   revenueDeposits   -- runFullModel's merge: each misc line at miscLeafKey (its target or rev_misc_<id>), then each
 *                        implementation line at implTargetKey (its target or rev_impl_<id>); forecast months; r2 per cell.
 *   revenueInjections -- the orchestrator: AE GAAP revenue at the AE target line, Company Quota GAAP at the CQ target line, then each
 *                        implementation line again (idempotent) -- forecast months (m > cutoff); r2 per cell.
 *   THE GATES are the callers': runFullModel passes the AE result only when the module is active (enabled AND its settings row
 *   exists -- C-3 / F9) and the CQ result only when CQ is enabled; the orchestrator injects what it is handed (the config re-run
 *   passes what ModelProvider stored). Disabled misc / implementation lines produce no series (their engines skip them).
 *
 * revenueByLeaf composes them for the vendor engine:
 *   byLeaf  { key: { month: value } } -- every line a revenue module forecasts onto, deposits then injections (later assigns win,
 *           the same precedence the IS sees);
 *   total   { month: value }          -- the Income Statement's Total Revenue: r2 of the sum over the skeleton's total_revenue.sumOf
 *           of r2(each line's forecast), a line no module forecasts read from the forecast snapshot -- computeIS's own rule (Pass 1
 *           r2 per leaf, Pass 2 sumOf). Forecast months only; history months are the stored actuals and no vendor rate touches them.
 * NO CIRCULARITY: no revenue engine reads an expense (AE: reps + quota; CQ: its settings; misc: its lines; implementation: new ACV /
 * misc), and the sweep's interest is Other Income, so the vendor engine may read this before it runs. A future revenue line driven
 * by a cost (e.g. re-billed expenses) would make a "% of Total Revenue" vendor circular and needs its own design.
 * Plain ASCII (section 12.5 rule 55). */

import { r2 } from './computeIS';
import { getModuleTargetLine } from './moduleWiring';
import { miscLeafKey } from '../utils/miscKeys';
import { implTargetKey } from '../utils/implKeys';

const fcSeries = (months, valueOf) => { const s = {}; months.forEach(m => { s[m] = r2(valueOf(m)); }); return s; };

/** runFullModel's merge layer: ordered [key, series] -- misc lines, then implementation lines -- over `forecastMonths`. */
export function revenueDeposits(miscRevenue, implementationRevenue, forecastMonths) {
  const fc = forecastMonths || [];
  const out = [];
  Object.entries(miscRevenue || {}).forEach(([lineId, lineData]) => {
    const key = miscLeafKey({ id: lineId, target_gl_line_key: lineData.target_gl_line_key });   // W-089: the one owner
    out.push([key, fcSeries(fc, m => (lineData.amounts || {})[m] || 0)]);
  });
  Object.entries(implementationRevenue || {}).forEach(([lineId, lineData]) => {
    out.push([implTargetKey(lineId, lineData.target_gl_line_key), fcSeries(fc, m => (lineData.gaapRevenue || {})[m] || 0)]);
  });
  return out;
}

/** The orchestrator's injection layer: ordered [key, series] -- AE, Company Quota, then implementation -- over the months after
 *  `cutoff`. A null module result (inactive) or an unwired module (no target line) injects nothing. */
export function revenueInjections({ aeQuotaDR, companyQuotaDR, implementationRevenue, revMappings, months, cutoff }) {
  const fc = (months || []).filter(m => m > cutoff);
  const out = [];
  const aeLine = getModuleTargetLine(revMappings, 'ae_quota');
  const cqLine = getModuleTargetLine(revMappings, 'company_quota');
  if (aeLine && aeQuotaDR) out.push([aeLine, fcSeries(fc, m => aeQuotaDR.gaapRevenue?.[m] || 0)]);
  if (cqLine && companyQuotaDR) out.push([cqLine, fcSeries(fc, m => companyQuotaDR.gaapRevenue?.[m] || 0)]);
  Object.entries(implementationRevenue || {}).forEach(([lineId, lineData]) => {
    out.push([implTargetKey(lineId, lineData.target_gl_line_key), fcSeries(fc, m => lineData.gaapRevenue?.[m] || 0)]);
  });
  return out;
}

/** The forecast revenue by IS line and the IS Total Revenue, before the vendor engine runs. Returns { byLeaf, total, deposits }
 *  (`deposits` is handed back so runFullModel's merge writes exactly what was summed here). */
export function revenueByLeaf({ isForecast, miscRevenue, implementationRevenue, aeQuotaDR, companyQuotaDR, revMappings, isLines, months, cutoff }) {
  const fc = (months || []).filter(m => m > cutoff);
  const deposits = revenueDeposits(miscRevenue, implementationRevenue, fc);
  const injections = revenueInjections({ aeQuotaDR, companyQuotaDR, implementationRevenue, revMappings, months, cutoff });
  const byLeaf = {};
  [...deposits, ...injections].forEach(([key, s]) => { byLeaf[key] = { ...(byLeaf[key] || {}), ...s }; });
  const totalLine = (isLines || []).find(l => l && l.id === 'total_revenue');
  const sumOf = (totalLine && totalLine.sumOf) || [];
  const snap = isForecast || {};
  const total = {};
  fc.forEach(m => {
    total[m] = r2(sumOf.reduce((acc, id) => acc + r2((byLeaf[id] ? byLeaf[id][m] : snap[id]?.[m]) || 0), 0));
  });
  return { byLeaf, total, deposits };
}
